import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Search, Check, Undo2, Loader2 } from 'lucide-react';
import { useInstitution } from '../../contexts/InstitutionContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissao } from '../../hooks/usePermissao';
import { Card, Input } from '../../Components/ui';
import { useToast } from '../../Components/Feedback';
import { useApi } from '../../api/useApi';

// "termos.assinado_em" vem do backend já como "AAAA-MM-DD" — só reformata
// pra exibição, sem conversão de fuso (mesmo padrão de GerenciarMatriculas.js).
const formatarDataBR = (dataISO) => {
  if (!dataISO) return null;
  const [ano, mes, dia] = String(dataISO).split('-');
  return dia && mes && ano ? `${dia}/${mes}/${ano}` : null;
};

// Detalhe de um termo: quem já assinou (com a data, desmarcável caso tenha
// sido engano) e quem ainda falta assinar (marcável com um clique) — só
// alunos ativos dos dois lados (ver backend/termos.js).
function TermoDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { institutionId } = useInstitution();
  const { token } = useAuth();
  const { podeEditar } = usePermissao('/termos');
  const queryClient = useQueryClient();
  const toast = useToast();

  const api = useApi();

  const { data: termo, isLoading } = useQuery({
    queryKey: ['termo', id],
    queryFn: async () => {
      const res = await api.get(`/api/termos/${id}`);
      return res?.data ?? res;
    },
    enabled: !!institutionId && !!token && !!id,
    refetchOnMount: 'always',
  });

  const recarregar = () => queryClient.invalidateQueries({ queryKey: ['termo', id] });
  const recarregarHome = () =>
    queryClient.invalidateQueries({ queryKey: ['termos', institutionId] });

  const [busca, setBusca] = useState('');
  const [processando, setProcessando] = useState(false);

  const filtrar = (lista) => {
    if (!busca.trim()) return lista;
    const termoBusca = busca.trim().toLowerCase();
    return lista.filter((a) => a.nome.toLowerCase().includes(termoBusca));
  };

  const marcarAssinado = async (aluno) => {
    setProcessando(true);
    try {
      const res = await api.post(`/api/termos/${id}/assinaturas`, { aluno_id: aluno.id });
      toast.success(res?.message || 'Assinatura registrada com sucesso.');
      recarregar();
      recarregarHome();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessando(false);
    }
  };

  const desmarcarAssinado = async (aluno) => {
    setProcessando(true);
    try {
      const res = await api.del(`/api/termos/${id}/assinaturas/${aluno.id}`);
      toast.success(res?.message || 'Assinatura removida com sucesso.');
      recarregar();
      recarregarHome();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessando(false);
    }
  };

  if (isLoading || !termo) {
    return (
      <div className="min-h-screen w-full bg-[#F9FAFB] dark:bg-gray-950 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  const pendentes = filtrar(termo.pendentes);
  const assinados = filtrar(termo.assinados);

  return (
    <div className="min-h-screen w-full bg-[#F9FAFB] dark:bg-gray-950">
      <main className="p-4 max-w-4xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/termos')}
            className="w-9 h-9 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center text-gray-500 dark:text-gray-400 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1
            className="text-xl font-semibold text-gray-900 dark:text-gray-100 truncate"
            translate="no"
          >
            {termo.nome}
          </h1>
        </div>

        <Input
          placeholder="Buscar aluno pelo nome..."
          icon={Search}
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <p className="text-xs font-medium text-amber-600 dark:text-amber-400 mb-2">
              Falta assinar ({pendentes.length})
            </p>
            <div className="space-y-1.5">
              {pendentes.length === 0 ? (
                <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-6">
                  {termo.pendentes.length === 0
                    ? 'Todo mundo já assinou.'
                    : 'Nenhum resultado pra essa busca.'}
                </p>
              ) : (
                pendentes.map((aluno) => (
                  <div
                    key={aluno.id}
                    className="flex items-center justify-between px-3 py-2 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/40"
                  >
                    <div className="min-w-0">
                      <span className="text-sm text-gray-900 dark:text-gray-100" translate="no">
                        {aluno.nome}
                      </span>
                      {(aluno.turno || aluno.turma) && (
                        <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">
                          {[aluno.turno, aluno.turma].filter(Boolean).join(' · ')}
                        </span>
                      )}
                    </div>
                    {podeEditar && (
                      <button
                        onClick={() => marcarAssinado(aluno)}
                        disabled={processando}
                        title="Marcar como assinado"
                        className="w-7 h-7 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/30 flex items-center justify-center text-gray-400 dark:text-gray-500 hover:text-green-600 dark:hover:text-green-400 transition-colors disabled:opacity-50 flex-shrink-0"
                      >
                        <Check className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          </Card>

          <Card>
            <p className="text-xs font-medium text-green-600 dark:text-green-400 mb-2">
              Já assinaram ({assinados.length})
            </p>
            <div className="space-y-1.5">
              {assinados.length === 0 ? (
                <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-6">
                  {termo.assinados.length === 0
                    ? 'Ninguém assinou ainda.'
                    : 'Nenhum resultado pra essa busca.'}
                </p>
              ) : (
                assinados.map((aluno) => (
                  <div
                    key={aluno.id}
                    className="flex items-center justify-between px-3 py-2 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/40"
                  >
                    <div className="min-w-0">
                      <span className="text-sm text-gray-900 dark:text-gray-100" translate="no">
                        {aluno.nome}
                      </span>
                      <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">
                        assinado em {formatarDataBR(aluno.assinado_em)}
                      </span>
                    </div>
                    {podeEditar && (
                      <button
                        onClick={() => desmarcarAssinado(aluno)}
                        disabled={processando}
                        title="Desfazer (marcado por engano)"
                        className="w-7 h-7 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 flex items-center justify-center text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 transition-colors disabled:opacity-50 flex-shrink-0"
                      >
                        <Undo2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          </Card>
        </div>
      </main>
    </div>
  );
}

export default TermoDetalhe;
